import geography from './scenery/geography.json' with { type: 'json' };

export const SCENERY_OPTIONS = {auto:'Automatic',plain:'Plain horizon',city:'City',mountains:'Mountains',hills:'Rolling hills',coast:'Coast',lake:'Lakeside',desert:'Desert',forest:'Forest',countryside:'Countryside',water:'Open water'};
// Reviewed associations: coordinates identify the local Natural Earth urban footprint,
// never a user's city-name string. Background hints are regional, not sight lines.
export const SCENERY_CITIES = [
  ['new-york','New York',40.7128,-74.006,'coast'],['brooklyn','Brooklyn',40.6782,-73.9442,'coast'],
  ['los-angeles','Los Angeles',34.0522,-118.2437,'mountains'],['miami','Miami',25.7617,-80.1918,'coast'],
  ['chicago','Chicago',41.8781,-87.6298,'lake'],['boston','Boston',42.3601,-71.0589,'coast'],
  ['toronto','Toronto',43.6532,-79.3832,'lake'],['montreal','Montreal',45.5017,-73.5673,'hills'],
  ['london','London',51.5074,-.1278,'countryside'],['paris','Paris',48.8566,2.3522,'countryside'],
  ['johannesburg','Johannesburg',-26.2041,28.0473,'hills'],['melbourne','Melbourne',-37.8136,144.9631,'coast'],
  ['sydney','Sydney',-33.8688,151.2093,'coast'],['jerusalem','Jerusalem',31.778,35.235,'hills'],
  ['tel-aviv','Tel Aviv',32.0853,34.7818,'coast'],['tzfat','Tzfat',32.9646,35.496,'mountains'],
];
const rad=Math.PI/180;
const plain={scene:'plain',foreground:null,label:'Plain horizon',basis:'No regional classification',assets:[]};
const longitudeDelta=(a,b)=>((a-b+540)%360)-180;
const decoded=new WeakMap();
function polygonRings(feature) {
  if(typeof feature[2][0]!=='string')return feature[2];
  if(decoded.has(feature))return decoded.get(feature);
  const rings=feature[2].map(text=>{let index=0,x=0,y=0;const points=[];
    const read=()=>{let result=0,shift=0,b;do{b=text.charCodeAt(index++)-63;result|=(b&31)<<shift;shift+=5;}while(b>=32&&index<text.length);return result&1?~(result>>>1):result>>>1;};
    while(index<text.length){x+=read();y+=read();points.push([x/1000,y/1000]);}return points;});
  decoded.set(feature,rings);return rings;
}
function ringContains(lon,lat,ring) {
  let inside=false;
  for(let i=0,j=ring.length-1;i<ring.length;j=i++) {
    const [x,y]=ring[i],[u,v]=ring[j];
    if((y>lat)!==(v>lat) && lon<(u-x)*(lat-y)/(v-y)+x)inside=!inside;
  }
  return inside;
}
export function distanceToPolygon(lon,lat,feature) {
  const rings=polygonRings(feature);
  if(ringContains(lon,lat,rings[0])&&!rings.slice(1).some(r=>ringContains(lon,lat,r)))return 0;
  const scale=Math.max(.0001,Math.cos(lat*rad))*111.195;
  let distance=Infinity;
  for(const ring of rings)for(let i=1;i<ring.length;i++) {
    const a=[longitudeDelta(ring[i-1][0],lon)*scale,(ring[i-1][1]-lat)*111.195];
    const b=[longitudeDelta(ring[i][0],lon)*scale,(ring[i][1]-lat)*111.195];
    const dx=b[0]-a[0],dy=b[1]-a[1],l=dx*dx+dy*dy,t=l?Math.max(0,Math.min(1,-(a[0]*dx+a[1]*dy)/l)):0;
    distance=Math.min(distance,Math.hypot(a[0]+t*dx,a[1]+t*dy));
  }
  return distance;
}
function candidates(lon,lat) {
  const ids=new Set();
  const range=Math.min(36,Math.ceil(.3/Math.max(.001,Math.cos(lat*rad))/5)+1);
  for(let x=Math.floor(lon/5)-range;x<=Math.floor(lon/5)+range;x++)for(let y=Math.floor(lat/5)-1;y<=Math.floor(lat/5)+1;y++) {
    const wrapped=((x+36)%72+72)%72-36;
    for(const id of geography.bins[`${wrapped},${y}`]||[])ids.add(id);
  }
  return [...ids];
}
let associations;
function cityAssociations(){return associations??=SCENERY_CITIES.map(city=>({city,ids:candidates(city[3],city[2]).filter(i=>geography.features[i][0]==='urban'&&distanceToPolygon(city[3],city[2],geography.features[i])<=5)}));}
function make(scene,foreground,basis,label) {
  const assets=[scene==='plain'?null:scene,foreground].filter(Boolean);
  return {scene,foreground,basis,label:label||SCENERY_OPTIONS[scene],assets:[...new Set(assets)].map(id=>`scenery/${id}.png`)};
}
// Conservative curated forest interiors. Regional illustrative coverage, not
// current land-cover data. No blanket latitude/continent guesses.
const forests=[
  ['Black Forest',[[7.8,47.8],[8.4,47.8],[8.6,48.7],[8.1,48.8],[7.8,47.8]]],
  ['Adirondack forest', [[-74.8,43.6],[-73.8,43.6],[-73.8,44.5],[-74.8,44.5],[-74.8,43.6]]],
  ['Central Amazon forest',[[-65,-5],[-61,-5],[-61,-2],[-65,-2],[-65,-5]]],
  ['Central Congo forest',[[21,-2],[25,-2],[25,2],[21,2],[21,-2]]],
];
export function resolveScenery(location, preference='auto') {
  const lat=location?.latitude,lon=location?.longitude;
  if(!Number.isFinite(lat)||!Number.isFinite(lon)||Math.abs(lat)>90||Math.abs(lon)>180)return {...plain,basis:'Location not set'};
  if(preference==='plain')return {...plain,basis:'Selected by you'};
  if(preference!=='auto'&&Object.hasOwn(SCENERY_OPTIONS,preference))return make(preference,null,'Selected by you');
  // The small Safed footprint is absent from the generalized urban dataset.
  // Conservative town-core outline; outside it use regional classification.
  if(ringContains(lon,lat,[[35.477,32.950],[35.500,32.947],[35.512,32.961],[35.505,32.978],[35.485,32.977],[35.477,32.950]]))
    return make('mountains','tzfat','Reviewed Tzfat town-core outline','Tzfat · Mountains');
  const near=candidates(lon,lat),distances=new Map();
  const distance=id=>{if(!distances.has(id))distances.set(id,distanceToPolygon(lon,lat,geography.features[id]));return distances.get(id);};
  const urban=near.filter(i=>geography.features[i][0]==='urban'&&distance(i)<=5);
  const city=cityAssociations().filter(a=>a.ids.some(i=>urban.includes(i))).sort((a,b)=>
    Math.hypot((a.city[3]-lon)*Math.cos(lat*rad),a.city[2]-lat)-Math.hypot((b.city[3]-lon)*Math.cos(lat*rad),b.city[2]-lat))[0]?.city;
  if(city)return make(city[4],city[0],'Associated urban footprint',`${city[1]} · ${SCENERY_OPTIONS[city[4]]}`);
  const nearest=(kind,km)=>near.filter(i=>geography.features[i][0]===kind&&distance(i)<=km).sort((a,b)=>distance(a)-distance(b))[0];
  const land=nearest('land',0); let scene='plain',basis='Regional classification unavailable';
  if(nearest('lake',10)!==undefined){scene='lake';basis='Within 10 km of a mapped lake';}
  else {
    // Land polygon distance is zero inland; use its boundary for coastal proximity.
    const coastal=near.some(i=>geography.features[i][0]==='land'&&polygonRings(geography.features[i]).some(r=>distanceToPolygon(lon,lat,['line',null,[r,r]])<=10));
    if(coastal){scene='coast';basis='Within 10 km of the mapped coast';}
    else if(land===undefined){scene='water';basis='Outside mapped land';}
    else if(nearest('mountains',25)!==undefined){scene='mountains';basis='Within 25 km of a mapped mountain region';}
    else if(nearest('desert',0)!==undefined){scene='desert';basis='Mapped desert region';}
    else if(forests.some(([,ring])=>ringContains(lon,lat,ring))){scene='forest';basis='Reviewed forest-region interior';}
    else if(nearest('hills',0)!==undefined){scene='hills';basis='Mapped foothills';}
    else if(nearest('countryside',0)!==undefined){scene='countryside';basis='Mapped plain';}
  }
  return make(scene,urban.length?'city':null,basis,urban.length?`City · ${SCENERY_OPTIONS[scene]}`:undefined);
}
